import { Koi } from '@ksyos/koi';
import type { Section } from '@common/formEngine/formEngine.types';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import type { NullablePrimitives } from '@common/utils/utilityTypes';
import { AssetCategory } from '@common/enums';
import { dependsOn } from '@common/formEngine/dependsOn';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import { OorZijde, oorZijdeOptions } from '../utils';
import { Beoordeling, beoordelingOptions, ToestemmingVerstrekking, toestemmingVerstrekkingOptions } from './utils';

export enum Specialist {
    audioloog = 'Audioloog',
    knoArts = 'KNO-arts',
}

const specialistOptions: Specialist[] = [Specialist.audioloog, Specialist.knoArts];

export type HearingLossAssessmentDataV2 = NullablePrimitives<{
    antwoorden_of_opmerkingen: string;
    beoordeling: Beoordeling;
    bezoek_aan_specialist_nodig: boolean;
    opmerkingen: string;
    patient_gesproken: boolean;
    recipe?: {
        assetId: string;
        filename: string;
    };
    reden_recept_anders: string;
    reden_recept_hoortoestel: string;
    reden_recept_soloapparatuur: string;
    reden_recept_wek_en_waarschuwingssysteem: string;
    specialist: string;
    specificatie_zijde: OorZijde;
    toelichting_beoordeling: string;
    toelichting_gesprek: string;
    toelichting_specificatie_doorverwijzing: string;
    toestemming_verstrekking: {
        anders: string | null;
        hoortoestel: boolean | null;
        soloapparatuur: boolean | null;
        wek_en_waarschuwingssysteem: boolean | null;
    };
}>;

type HearingLossAssessmentReadDataV2 = {
    assets?: never;
};

export type HearingLossAssessmentKeyV2 = DeepPickPath<HearingLossAssessmentDataV2 & HearingLossAssessmentReadDataV2>;

export const hearingLossAssessmentSectionsV2: Section<HearingLossAssessmentDataV2 & HearingLossAssessmentReadDataV2>[] =
    [
        {
            fields: [
                {
                    draftSchema: Koi.koi().enum(Beoordeling).allow(null),
                    finalSchema: Koi.koi().enum(Beoordeling),
                    isRequired: true,
                    key: 'beoordeling',
                    label: 'Beoordeling',
                    type: 'radioGroup',
                    valueOptions: beoordelingOptions,
                },
                {
                    isRequired: true,
                    key: 'toelichting_beoordeling',
                    label: 'Toelichting op beoordeling voor patiënt',
                    type: 'textArea',
                },
            ],
            title: 'Conclusie op basis van onderzoek',
        },
        {
            fields: [
                {
                    checkboxEnumType: ToestemmingVerstrekking,
                    isRequired: true,
                    key: 'toestemming_verstrekking',
                    label: 'Toestemming verstrekking',
                    type: 'checkboxGroup',
                    valueOptions: toestemmingVerstrekkingOptions,
                },
                {
                    draftSchema: Koi.when('toestemming_verstrekking.hoortoestel', {
                        is: true,
                        otherwise: null,
                        then: Koi.koi().enum(OorZijde).allow(null),
                    }),
                    finalSchema: Koi.when('toestemming_verstrekking.hoortoestel', {
                        is: true,
                        otherwise: null,
                        then: Koi.koi().enum(OorZijde),
                    }),
                    include: dependsOn(
                        'toestemming_verstrekking',
                        ({ toestemming_verstrekking }) => !!toestemming_verstrekking.hoortoestel
                    ),
                    isRequired: true,
                    key: 'specificatie_zijde',
                    label: 'Specificatie zijde',
                    type: 'radioGroup',
                    valueOptions: oorZijdeOptions,
                },
                {
                    draftSchema: Koi.when('toestemming_verstrekking.wek_en_waarschuwingssysteem', {
                        is: true,
                        otherwise: null,
                        then: Koi.string().allow(null),
                    }),
                    finalSchema: Koi.when('toestemming_verstrekking.wek_en_waarschuwingssysteem', {
                        is: true,
                        otherwise: null,
                        then: Koi.string(),
                    }),
                    include: dependsOn(
                        'toestemming_verstrekking',
                        ({ toestemming_verstrekking }) => !!toestemming_verstrekking.wek_en_waarschuwingssysteem
                    ),
                    initialValue: null,
                    isRequired: true,
                    key: 'reden_recept_wek_en_waarschuwingssysteem',
                    label: 'Reden recept wek- en waarschuwingssysteem',
                    type: 'custom',
                },
                {
                    draftSchema: Koi.when('toestemming_verstrekking.soloapparatuur', {
                        is: true,
                        otherwise: null,
                        then: Koi.string().allow(null),
                    }),
                    finalSchema: Koi.when('toestemming_verstrekking.soloapparatuur', {
                        is: true,
                        otherwise: null,
                        then: Koi.string(),
                    }),
                    include: dependsOn(
                        'toestemming_verstrekking',
                        ({ toestemming_verstrekking }) => !!toestemming_verstrekking.soloapparatuur
                    ),
                    initialValue: null,
                    isRequired: true,
                    key: 'reden_recept_soloapparatuur',
                    label: 'Reden recept soloapparatuur',
                    type: 'custom',
                },
                {
                    draftSchema: Koi.when('toestemming_verstrekking.hoortoestel', {
                        is: true,
                        otherwise: null,
                        then: Koi.string().allow(null),
                    }),
                    finalSchema: Koi.when('toestemming_verstrekking.hoortoestel', {
                        is: true,
                        otherwise: null,
                        then: Koi.string(),
                    }),
                    include: dependsOn(
                        'toestemming_verstrekking',
                        ({ toestemming_verstrekking }) => !!toestemming_verstrekking.hoortoestel
                    ),
                    isRequired: true,
                    key: 'reden_recept_hoortoestel',
                    label: 'Reden recept hoortoestel',
                    type: 'textArea',
                },
                {
                    draftSchema: Koi.when('toestemming_verstrekking.anders', {
                        is: Koi.string(),
                        otherwise: null,
                        then: Koi.string().allow(null),
                    }),
                    finalSchema: Koi.when('toestemming_verstrekking.anders', {
                        is: Koi.string().required(),
                        otherwise: null,
                        then: Koi.string(),
                    }),
                    include: dependsOn(
                        'toestemming_verstrekking',
                        ({ toestemming_verstrekking }) => !!toestemming_verstrekking.anders
                    ),
                    isRequired: true,
                    key: 'reden_recept_anders',
                    label: 'Reden recept anders',
                    type: 'textArea',
                },
                {
                    key: 'opmerkingen',
                    label: 'Opmerkingen recept',
                    type: 'textArea',
                },
            ],
            include: dependsOn('beoordeling', ({ beoordeling }) => beoordeling === Beoordeling.pluis),
            title: 'Specificaties recept',
        },
        {
            fields: [
                {
                    draftSchema: Koi.when('beoordeling', {
                        is: Beoordeling.nietPluis,
                        otherwise: null,
                        then: Koi.boolean().allow(null),
                    }),
                    finalSchema: Koi.when('beoordeling', {
                        is: Beoordeling.nietPluis,
                        otherwise: null,
                        then: Koi.boolean(),
                    }),
                    isRequired: true,
                    key: 'bezoek_aan_specialist_nodig',
                    label: 'Bezoek aan specialist nodig',
                    type: 'boolean',
                },
                {
                    allowOtherOption: true,
                    draftSchema: Koi.when('bezoek_aan_specialist_nodig', {
                        is: true,
                        otherwise: null,
                        then: getTextAreaStringSchema(true),
                    }),
                    finalSchema: Koi.when('bezoek_aan_specialist_nodig', {
                        is: true,
                        otherwise: null,
                        then: getTextAreaStringSchema(),
                    }),
                    include: dependsOn('bezoek_aan_specialist_nodig'),
                    isRequired: true,
                    key: 'specialist',
                    label: 'Specialist',
                    type: 'radioGroup',
                    valueOptions: specialistOptions,
                },
                {
                    draftSchema: Koi.when('beoordeling', {
                        is: Beoordeling.nietPluis,
                        otherwise: null,
                        then: getTextAreaStringSchema(true),
                    }),
                    finalSchema: Koi.when('beoordeling', {
                        is: Beoordeling.nietPluis,
                        otherwise: null,
                        then: getTextAreaStringSchema(),
                    }),
                    isRequired: true,
                    key: 'toelichting_specificatie_doorverwijzing',
                    label: 'Toelichting',
                    type: 'textArea',
                },
            ],
            include: dependsOn('beoordeling', ({ beoordeling }) => beoordeling === Beoordeling.nietPluis),
            title: 'Specificatie doorverwijzing',
        },
        {
            fields: [
                {
                    draftSchema: Koi.when('beoordeling', {
                        not: Beoordeling.nietTeBeoordelen,
                        otherwise: null,
                        then: Koi.boolean().allow(null),
                    }),
                    finalSchema: Koi.when('beoordeling', {
                        not: Beoordeling.nietTeBeoordelen,
                        otherwise: null,
                        then: Koi.boolean(),
                    }),
                    isRequired: true,
                    key: 'patient_gesproken',
                    label: 'Patiënt gesproken',
                    type: 'boolean',
                },
                {
                    draftSchema: Koi.when('patient_gesproken', {
                        is: true,
                        otherwise: null,
                        then: getTextAreaStringSchema(true),
                    }),
                    finalSchema: Koi.when('patient_gesproken', {
                        is: true,
                        otherwise: null,
                        then: getTextAreaStringSchema(true),
                    }),
                    include: dependsOn('patient_gesproken'),
                    key: 'toelichting_gesprek',
                    label: 'Toelichting op gesprek',
                    type: 'textArea',
                },
            ],
            include: dependsOn(
                'beoordeling',
                ({ beoordeling }) => beoordeling !== null && beoordeling !== Beoordeling.nietTeBeoordelen
            ),
            title: 'Contact met patiënt',
        },
        {
            fields: [
                {
                    key: 'antwoorden_of_opmerkingen',
                    label: 'Antwoorden of opmerkingen',
                    type: 'textArea',
                },
            ],
            title: 'Bericht aan audicien',
        },
        {
            fields: [
                {
                    category: AssetCategory.recipe,
                    draftSchema: Koi.optional(),
                    finalSchema: Koi.optional(),
                    isReadonly: true,
                    key: 'assets',
                    label: 'Recept',
                    type: 'assets',
                },
            ],
            title: 'Recept',
        },
    ];
